/**
 * FX Trader — dedicated FX trading workspace.
 *
 * Architecture:
 *   - Reuses TopBar, LeftRail, BrokerClient, and WsClient for consistency.
 *   - FX instruments: synthetic NGN pairs (USD/NGN, EUR/NGN, GBP/NGN, etc.)
 *   - Supports full order entry flow with real-time quotes and positions.
 *   - Protected by the same LegalGate that enforces FX Risk Disclosure acknowledgment.
 *   - Session token set in App.tsx ensures FX pages (if separate) can verify access.
 *
 * FX-specific:
 *   - 22-pip headline spreads on USD/NGN
 *   - Tier-A African crosses (supported pairs)
 *   - Non-standard lot sizing (not face value like bonds)
 *   - Fast execution with minimal latency
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { BrokerClient, BrokerApiError } from '../api/brokerClient';
import { WsClient, type WsEvent, type WsStatus } from '../api/wsClient';
import { TopBar } from './TopBar';
import { BROKERS, SOR_BROKER, type BrokerId } from '../lib/brokers';
import { LeftRail, type RailView } from './LeftRail';
import { AccountSummary } from './AccountSummary';
import { EventsFeed } from './EventsFeed';
import { PositionsTable, type PositionRow } from './PositionsTable';
import { Chart, type ChartSettings } from './Chart';
import type { BalanceRow } from './AccountBar';
import { newId } from '../lib/format';
import { useLiveMarketData, type TradeExecuted, type LiveMarketData } from '../hooks/useLiveMarketData';
import { LiveTrades, type ExecutedTrade } from './LiveTrades';
import { TradeConfirmationPopup } from './TradeConfirmationPopup';

const BROKER_ID = (import.meta as any).env?.VITE_BROKER_ID ?? 'mock';
const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '/api/v1';
const WS_ENDPOINT = (import.meta as any).env?.VITE_WS_ENDPOINT ?? '';

// Supported FX pairs
const FX_PAIRS = ['USD/NGN', 'EUR/NGN', 'GBP/NGN', 'CHF/NGN', 'JPY/NGN', 'CAD/NGN'];

interface Props {
  brokerOverride?: BrokerClient;
  wsOverride?: WsClient;
  onExitToEquities?: () => void;
  onExitToWelcome?: () => void;
  onSwitchView?: (v: RailView) => void;
}

export function FXTrader({ brokerOverride, wsOverride, onExitToEquities, onExitToWelcome, onSwitchView }: Props = {}) {
  const auth = useAuth();
  const [activeBroker, setActiveBroker] = useState<BrokerId>('sor');
  const broker = useMemo(() => brokerOverride ?? new BrokerClient({
    baseUrl: API_BASE, getIdToken: auth.getIdToken,
  }), [auth.getIdToken, brokerOverride]);

  const [accountId, setAccountId] = useState<string | undefined>();
  const [accountLabel, setAccountLabel] = useState<string>('FX Account');
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [balance, setBalance] = useState<BalanceRow | undefined>();
  const [events, setEvents] = useState<Array<{ at: string; e: WsEvent }>>([]);
  const [wsStatus, setWsStatus] = useState<WsStatus>('closed');
  const [err, setErr] = useState<string | undefined>();
  const wsRef = useRef<WsClient | undefined>();

  const [selectedPair, setSelectedPair] = useState<string>('USD/NGN');
  const [side, setSide] = useState<'BUY' | 'SELL'>('BUY');
  const [lotSize, setLotSize] = useState<string>('100000');
  const [marketTime, setMarketTime] = useState<string>('14:30 WAT');
  const [submitting, setSubmitting] = useState(false);
  const [last, setLast] = useState<{ brokerOrderId: string } | undefined>();
  const [reviewing, setReviewing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Live trading demo state
  const [executedTrades, setExecutedTrades] = useState<ExecutedTrade[]>([]);
  const [demoBalance, setDemoBalance] = useState<bigint>(BigInt(500_000_000)); // 5M NGN in kobo
  const [showTradeConfirm, setShowTradeConfirm] = useState(false);
  const [lastTradeInfo, setLastTradeInfo] = useState<TradeExecuted | undefined>();
  const marketDataRef = useRef<LiveMarketData>({
    candles: [],
    lastPrice: 1567.50,
    bid: 1567.28,
    ask: 1567.50,
    bidSize: 10000,
    askSize: 10000,
    volume: 0,
    dayHigh: 1567.50,
    dayLow: 1567.50,
    pctChange: 0,
    volumeTotal: 0,
  });

  const pairBasePrices: Record<string, number> = {
    'USD/NGN': 1567.50,
    'EUR/NGN': 1720.00,
    'GBP/NGN': 1980.00,
    'CHF/NGN': 1750.00,
    'JPY/NGN': 10.50,
    'CAD/NGN': 1150.00,
  };

  const marketData = useLiveMarketData({
    symbol: selectedPair,
    basePrice: pairBasePrices[selectedPair] ?? 1567.50,
    onTradeExecuted: (trade) => {
      // Update demo balance
      const notionalKobo = BigInt(Math.floor(trade.notional * 100));
      if (trade.side === 'BUY') {
        setDemoBalance((prev) => prev - notionalKobo);
      } else {
        setDemoBalance((prev) => prev + notionalKobo);
      }
    },
    updateIntervalMs: 1000,
  });

  // Store for quick access in chart rendering
  useEffect(() => {
    marketDataRef.current = marketData;
  }, [marketData]);

  // Nav history for TopBar Back/Home/Close parity with equities
  const [view, setView] = useState<RailView>('Trade');
  const [viewHistory, setViewHistory] = useState<RailView[]>([]);
  const navigateTo = useCallback((next: RailView) => {
    setView((prev) => { if (prev !== next) setViewHistory((h) => [...h, prev]); return next; });
  }, []);
  const goBack = useCallback(() => {
    setViewHistory((h) => {
      if (h.length === 0) { onExitToEquities?.(); return h; }
      const prev = h[h.length - 1]!;
      setView(prev);
      return h.slice(0, -1);
    });
  }, [onExitToEquities]);

  // Accounts bootstrap
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await broker.accounts(BROKER_ID);
        if (cancelled) return;
        const first = r.accounts?.[0];
        if (!first) { setErr('No linked broker accounts. Link one in Settings.'); return; }
        setAccountId(String(first.accountId));
        setAccountLabel((first.displayName ?? first.accountId) + ' (FX)');
      } catch (e: any) {
        setErr(e?.message ?? 'failed to load accounts');
      }
    })();
    return () => { cancelled = true; };
  }, [broker]);

  // Handle resize for mobile responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const [p, b] = await Promise.all([
        broker.positions(BROKER_ID, accountId),
        broker.balances(BROKER_ID, accountId),
      ]);
      setPositions((p.positions ?? []) as PositionRow[]);
      setBalance(((b.balances ?? [])[0]) as BalanceRow | undefined);
    } catch { /* transient */ }
  }, [broker, accountId]);
  useEffect(() => { void refresh(); }, [refresh]);

  // WebSocket for FX fills
  useEffect(() => {
    const ws = wsOverride ?? (WS_ENDPOINT ? new WsClient({ endpoint: WS_ENDPOINT, getIdToken: auth.getIdToken }) : undefined);
    if (!ws) return;
    wsRef.current = ws;
    const off1 = ws.on((e) => {
      setEvents((prev) => [...prev, { at: new Date().toISOString(), e }]);
      if (e.type === 'FillEmitted' || e.type === 'PositionUpdated') void refresh();
    });
    const off2 = ws.onStatus(setWsStatus);
    void ws.connect();
    return () => { off1(); off2(); ws.close(); };
  }, [auth.getIdToken, refresh, wsOverride]);

  // Market clock tick
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setMarketTime(`${String(d.getUTCHours() + 1).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} WAT`);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const lotNum = Number(lotSize) || 0;
  const validationErr = !Number.isFinite(lotNum) || lotNum <= 0
    ? 'Enter a valid lot size'
    : undefined;

  async function place() {
    if (validationErr || !accountId) return;
    setSubmitting(true);
    setErr(undefined);
    try {
      // Execute demo trade
      const executionPrice = side === 'BUY' ? marketData.ask : marketData.bid;
      const tradeId = newId('trade');
      const notional = lotNum * executionPrice;
      const notionalKobo = BigInt(Math.floor(notional * 100));

      // CRITICAL: Check negative balance guard for BUY trades
      if (side === 'BUY' && demoBalance < notionalKobo) {
        setErr(`Insufficient funds: Need ₦${(notional / 100).toLocaleString('en-NG')} but only have ₦${(Number(demoBalance) / 100).toLocaleString('en-NG')}`);
        setSubmitting(false);
        return;
      }

      const executedTrade: ExecutedTrade = {
        id: tradeId,
        symbol: selectedPair,
        side,
        quantity: lotNum,
        price: executionPrice,
        timestamp: Date.now(),
        notional: notional,
      };

      setExecutedTrades((prev) => [executedTrade, ...prev]);
      setLastTradeInfo({
        symbol: selectedPair,
        side,
        quantity: lotNum,
        price: executionPrice,
        timestamp: Date.now(),
        notional: notional,
      });
      setShowTradeConfirm(true);

      // Update demo balance after trade confirms
      if (side === 'BUY') {
        setDemoBalance((prev) => prev - notionalKobo);
      } else {
        setDemoBalance((prev) => prev + notionalKobo);
      }

      // Also try the real broker order for audit trail
      const payload: Record<string, unknown> = {
        clOrdId: newId('clord'),
        idempotencyKey: newId('idem'),
        accountId: accountId!,
        symbol: selectedPair,
        side,
        type: 'MARKET',
        quantity: lotNum,
        timeInForce: 'GTC',
        securityType: 'FX',
      };
      try {
        const ack = await broker.placeOrder(BROKER_ID, payload);
        setLast({ brokerOrderId: String(ack.brokerOrderId) });
      } catch {
        // Demo mode - broker call optional
      }

      setReviewing(false);
      void refresh();
    } catch (e) {
      if (e instanceof BrokerApiError) {
        setErr('Order rejected: ' + e.code);
      } else setErr('Network error placing FX order.');
    } finally {
      setSubmitting(false);
    }
  }

  // Quick trade execution from chart
  const executeQuickTrade = useCallback(
    (quickSide: 'BUY' | 'SELL', symbol: string, price: number) => {
      setSide(quickSide);
      setLotSize('50000');
      setSelectedPair(symbol);
      setReviewing(false);
      // Immediately execute the trade with the quick side and size
      // Use setTimeout to ensure state updates are applied first
      setTimeout(async () => {
        const notional = 50000 * price;
        const notionalKobo = BigInt(Math.floor(notional * 100));

        // Check balance
        if (quickSide === 'BUY' && demoBalance < notionalKobo) {
          setErr(`Insufficient funds for quick trade`);
          return;
        }

        const tradeId = newId('trade');
        const executedTrade: ExecutedTrade = {
          id: tradeId,
          symbol: symbol,
          side: quickSide,
          quantity: 50000,
          price: quickSide === 'BUY' ? marketData.ask : marketData.bid,
          timestamp: Date.now(),
          notional: notional,
        };

        setExecutedTrades((prev) => [executedTrade, ...prev]);
        setLastTradeInfo({
          symbol: symbol,
          side: quickSide,
          quantity: 50000,
          price: quickSide === 'BUY' ? marketData.ask : marketData.bid,
          timestamp: Date.now(),
          notional: notional,
        });
        setShowTradeConfirm(true);

        // Update balance
        if (quickSide === 'BUY') {
          setDemoBalance((prev) => prev - notionalKobo);
        } else {
          setDemoBalance((prev) => prev + notionalKobo);
        }
      }, 100);
    },
    [demoBalance, marketData.ask, marketData.bid]
  );

  const handleResetDemo = useCallback(() => {
    setExecutedTrades([]);
    setDemoBalance(BigInt(500_000_000));
    setLastTradeInfo(undefined);
    setShowTradeConfirm(false);
  }, []);

  /**
   * DEMO MODE P/L CALCULATIONS
   * These are approximations for demo purposes only.
   * pnlOpen: Sum of BUY notionals - Sum of SELL notionals (not actual P/L)
   * pnlDay: Approximate daily P/L using 0.1% multiplier on trades from last 24h
   *
   * NOTE: These do NOT account for entry/exit prices, margin multipliers, or actual market movement.
   * In production, P/L would be calculated as: (position qty × current price) - cost basis
   */
  const positionsValueKobo = useMemo(() =>
    String(BigInt(executedTrades.reduce((sum, t) => sum + t.notional, 0) * 100)),
    [executedTrades]
  );

  const pnlOpenKobo = useMemo(() =>
    String(
      BigInt(executedTrades.filter(t => t.side === 'BUY').reduce((sum, t) => sum + t.notional, 0) * 100) -
      BigInt(executedTrades.filter(t => t.side === 'SELL').reduce((sum, t) => sum + t.notional, 0) * 100)
    ),
    [executedTrades]
  );

  const pnlDayKobo = useMemo(() =>
    String(
      BigInt(
        executedTrades.reduce((sum, t) => {
          const hoursSinceStart = (Date.now() - t.timestamp) / (1000 * 60 * 60);
          if (hoursSinceStart < 24) {
            return sum + (t.side === 'BUY' ? -t.notional : t.notional) * 0.001;
          }
          return sum;
        }, 0) * 100
      )
    ),
    [executedTrades]
  );

  const tickerStrip = [
    { sym: 'USD/NGN', px: marketData.lastPrice.toFixed(2), pct: marketData.pctChange },
    { sym: selectedPair, px: marketData.lastPrice.toFixed(2), pct: marketData.pctChange },
  ];

  const chartCell = {
    symbol: selectedPair,
    symbolLongName: selectedPair + ' Synthetic (LIVE DEMO)',
    price: marketData.lastPrice,
    pctChange: marketData.pctChange,
    candles: marketData.candles,
    bid: marketData.bid,
    ask: marketData.ask,
    bidSize: marketData.bidSize,
    askSize: marketData.askSize,
    alerts: [{ price: (pairBasePrices[selectedPair] ?? 1567.50) * 1.02, direction: 'above' as const, label: 'Target' }],
  };

  return (
    <div className="app fx-app">
      <TopBar
        marketStatus="OPEN"
        marketTime={marketTime}
        tickers={tickerStrip}
        wsStatus={wsStatus}
        mfaSatisfied={auth.mfaSatisfied}
        username={auth.username}
        onSignOut={auth.signOut}
        activeBroker={activeBroker}
        onChangeBroker={(id) => setActiveBroker(id as BrokerId)}
        brokerOptions={[
          { id: SOR_BROKER.id, label: SOR_BROKER.label, glyph: SOR_BROKER.logoGlyph },
          ...BROKERS.map((b) => ({ id: b.id, label: b.label, glyph: b.logoGlyph })),
        ]}
        activeNav="Markets"
        onNav={(k) => {
          if (k === 'Dashboard') onSwitchView && onSwitchView('Dashboard');
          else if (k === 'Orders') onSwitchView && onSwitchView('Orders');
          else if (k === 'Portfolio') onSwitchView && onSwitchView('Monitor');
          else if (k === 'Markets') onSwitchView && onSwitchView('Trade');
        }}
        canGoBack={viewHistory.length > 0 || !!onExitToEquities}
        onBack={goBack}
        onHome={onExitToEquities}
        onClose={() => auth.signOut()}
      />
      {onExitToWelcome && (
        <div style={{ padding: '8px 16px', background: '#1a1f2e', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#ff7a3d', letterSpacing: '1px' }}>FX TRADER</h2>
          <button
            onClick={onExitToWelcome}
            style={{
              background: 'none',
              border: 'none',
              color: '#66b3ff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              padding: '4px 8px',
              marginLeft: 'auto',
            }}
          >
            ← Back to Welcome
          </button>
        </div>
      )}
      <div className="workspace">
        <LeftRail active={view} onChange={(v) => {
          if (v === 'FX') return;
          if (onSwitchView) onSwitchView(v);
          else navigateTo(v);
        }} />
        <div className="left-col">
          <AccountSummary
            balance={balance && {
              ...balance,
              accountId: balance.accountId!,
              currency: balance.currency ?? 'NGN',
              cash: String(demoBalance),
              buyingPower: String(demoBalance),
            }}
            accountLabel={accountLabel + ' (DEMO)'}
            positionsValueKobo={positionsValueKobo}
            pnlOpenKobo={pnlOpenKobo}
            pnlDayKobo={pnlDayKobo}
          />

          <section className="watchlist" aria-label="FX pairs watchlist">
            <div className="head">
              <h3>FX Pairs</h3>
              <div className="actions">
                <span className="muted">Live 24h</span>
              </div>
            </div>
            <div className="fx-list" role="list">
              {FX_PAIRS.map((pair) => (
                <button
                  key={pair}
                  type="button"
                  className={'fx-row ' + (pair === selectedPair ? 'active' : '')}
                  onClick={() => setSelectedPair(pair)}
                >
                  <span className="fx-row-pair">{pair}</span>
                  <span className="fx-row-px mono">1567.50</span>
                  <span className="fx-row-pct up mono">+0.08%</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="main-col">
          <div className="workarea">
            <div className="workarea-head">
              <span className="tab active">FX Trader</span>
              <span className="muted" style={{ marginLeft: 'auto', paddingRight: 12 }}>24/5 Trading &middot; SEC/NGX regulated &middot; NDPR</span>
            </div>
            <div className="workarea-body" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 320px', gap: 12, alignItems: 'start' }}>
              <div>
                <div className="panel" style={{ padding: 12 }}>
                  <div className="fx-head">
                    <div>
                      <div className="fx-head-pair">{selectedPair}</div>
                      <div className="fx-head-meta">
                        <span>Synthetic USD/NGN at 22-pip spreads</span>
                        <span>Last: 1567.50 <span className="up">+1.25 (+0.08%)</span></span>
                      </div>
                    </div>
                    <div className="fx-head-metrics">
                      <div><span className="k">Bid</span><span className="v mono">{marketData.bid.toFixed(2)}</span></div>
                      <div><span className="k">Ask</span><span className="v mono">{marketData.ask.toFixed(2)}</span></div>
                      <div><span className="k">Spread</span><span className="v mono">{((marketData.ask - marketData.bid) * 100).toFixed(1)} pips</span></div>
                      <div><span className="k">High</span><span className="v mono">{marketData.dayHigh.toFixed(2)}</span></div>
                      <div><span className="k">Low</span><span className="v mono">{marketData.dayLow.toFixed(2)}</span></div>
                    </div>
                  </div>
                </div>
                <div className="panel" style={{ padding: 0, overflow: 'hidden', height: 360, marginTop: 8 }}>
                  <Chart {...chartCell} height={360} onQuickTrade={executeQuickTrade} />
                </div>
                <LiveTrades trades={executedTrades} onReset={handleResetDemo} />
                <PositionsTable rows={positions} />
                <EventsFeed events={events} />
                {err && <div className="panel" style={{ borderColor: 'var(--red)' }}>{err}</div>}
              </div>

              {/* FX order ticket */}
              <div className="ticket-v2 fx-ticket" role="region" aria-label="FX Order Entry">
                <h3 className="te-title">FX ORDER ENTRY</h3>

                <div className="te-side" role="radiogroup" aria-label="Side">
                  <button type="button" className={'te-side-btn buy ' + (side === 'BUY' ? 'active' : '')} onClick={() => setSide('BUY')}>BUY</button>
                  <button type="button" className={'te-side-btn sell ' + (side === 'SELL' ? 'active' : '')} onClick={() => setSide('SELL')}>SELL</button>
                </div>

                <label className="te-label" htmlFor="fx-pair">PAIR</label>
                <select id="fx-pair" className="te-input" value={selectedPair} onChange={(e) => setSelectedPair(e.target.value)}>
                  {FX_PAIRS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>

                <label className="te-label" htmlFor="fx-lot">LOT SIZE</label>
                <input id="fx-lot" className="te-input" inputMode="numeric"
                  value={lotSize}
                  onChange={(e) => setLotSize(e.target.value.replace(/[^\d]/g, ''))} />
                <div className="te-hint">Min: 10,000 &middot; Typical: 100,000</div>

                <div className="te-fees" role="region" aria-label={'FX fees on ' + side}>
                  <div className="te-fees-h">FX QUOTE ({side}) - LIVE</div>
                  <FeeRow k="Rate" v={side === 'BUY' ? marketData.ask.toFixed(2) : marketData.bid.toFixed(2)} />
                  <FeeRow k="Notional (NGN)" v={(lotNum * (side === 'BUY' ? marketData.ask : marketData.bid)).toLocaleString('en-NG')} />
                  <FeeRow k="Margin Required" v={(lotNum * (side === 'BUY' ? marketData.ask : marketData.bid) * 0.02).toLocaleString('en-NG')} />
                </div>

                {validationErr && <div className="te-hint">{validationErr}</div>}
                {last && <div className="te-hint">Acked. broker_order_id={last.brokerOrderId}</div>}

                <div className="te-actions">
                  <button type="button" className={'te-review ' + (side === 'SELL' ? 'sell' : 'buy')}
                    disabled={submitting || !!validationErr} onClick={() => setReviewing(true)}>REVIEW ORDER</button>
                  <button type="button" className={'te-skip ' + (side === 'SELL' ? 'sell' : 'buy')}
                    disabled={submitting || !!validationErr} onClick={() => void place()}>
                    {submitting ? 'Placing…' : `${side} ${lotNum.toLocaleString('en-NG')} ${selectedPair} (Market)`}
                  </button>
                </div>

                {reviewing && (
                  <div className="te-modal" role="dialog" aria-modal="true" aria-label="Review FX order">
                    <div className="te-modal-card">
                      <h4>Confirm {side} &middot; {selectedPair}</h4>
                      <p className="te-modal-line"><span>Pair:</span> <strong>{selectedPair}</strong></p>
                      <p className="te-modal-line"><span>Lot Size:</span> <strong>{lotNum.toLocaleString('en-NG')}</strong></p>
                      <p className="te-modal-line"><span>Rate:</span> <strong>{(side === 'BUY' ? marketData.ask : marketData.bid).toFixed(2)}</strong></p>
                      <p className="te-modal-line"><span>Notional:</span> <strong>{(lotNum * (side === 'BUY' ? marketData.ask : marketData.bid)).toLocaleString('en-NG')} NGN</strong></p>
                      <p className="te-modal-line"><span>Margin:</span> <strong>{(lotNum * (side === 'BUY' ? marketData.ask : marketData.bid) * 0.02).toLocaleString('en-NG')} NGN</strong></p>
                      <div className="te-modal-buttons">
                        <button onClick={() => setReviewing(false)} disabled={submitting}>Cancel</button>
                        <button className={'te-confirm ' + (side === 'SELL' ? 'sell' : 'buy')} onClick={() => void place()} disabled={submitting}>
                          {submitting ? 'Placing…' : 'Confirm ' + side}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <TradeConfirmationPopup
                  symbol={lastTradeInfo?.symbol ?? ''}
                  side={lastTradeInfo?.side ?? 'BUY'}
                  quantity={lastTradeInfo?.quantity ?? 0}
                  price={lastTradeInfo?.price ?? 0}
                  timestamp={lastTradeInfo?.timestamp}
                  notional={lastTradeInfo?.notional}
                  isVisible={showTradeConfirm}
                  onClose={() => setShowTradeConfirm(false)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeeRow({ k, v, bold, className }: { k: string; v: string; bold?: boolean; className?: string }) {
  return (
    <div className={'fee-row ' + (className ?? '')} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: bold ? 600 : 400 }}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}

